import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { TrustedContact } from '../types';
import { api } from '../utils/api';
import toast from 'react-hot-toast';

interface TrustedContactSelectorProps {
  selectedContactId: number | '';
  onContactChange: (contactId: number) => void;
  onAddNewContact: () => void;
}

interface TrustedContactFormData {
  name: string;
  title: string;
  email: string;
  phone_number: string;
}

const TrustedContactSelector: React.FC<TrustedContactSelectorProps> = ({
  selectedContactId,
  onContactChange,
  onAddNewContact,
}) => {
  const [contacts, setContacts] = useState<TrustedContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<TrustedContact | null>(null);
  const [formData, setFormData] = useState<TrustedContactFormData>({
    name: '',
    title: '',
    email: '',
    phone_number: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchContacts = async () => {
    try {
      const response = await api.get('/trusted-contacts');
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to load customer contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  // Auto-select first contact if none is selected
  useEffect(() => {
    if (!loading && contacts.length > 0 && !selectedContactId) {
      onContactChange(contacts[0].id);
    }
  }, [loading, contacts, selectedContactId, onContactChange]);

  const handleOpenModal = (contact?: TrustedContact) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        name: contact.name,
        title: contact.title || '',
        email: contact.email,
        phone_number: contact.phone_number || '',
      });
    } else {
      setEditingContact(null);
      setFormData({
        name: '',
        title: '',
        email: '',
        phone_number: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingContact(null);
    setFormData({
      name: '',
      title: '',
      email: '',
      phone_number: '',
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (editingContact) {
        // Update existing contact
        await api.put(`/trusted-contacts/${editingContact.id}`, formData);
        toast.success('Contact updated successfully');
      } else {
        // Create new contact
        await api.post('/trusted-contacts', formData);
        toast.success('Contact added successfully');
      }
      
      await fetchContacts();
      handleCloseModal();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save contact');
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = () => {
    return formData.name.trim() !== '' && formData.email.trim() !== '';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Customer Contact
      </Typography>

      {contacts.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          You don't have any customer contacts yet. Add one to continue with your order.
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Choose a customer contact who can be reached regarding this order.
        </Typography>
      )}

      <RadioGroup
        value={selectedContactId}
        onChange={(e) => onContactChange(Number(e.target.value))}
        sx={{ mb: 3 }}
      >
        {contacts.map((contact) => (
          <Paper
            key={contact.id}
            variant="outlined"
            sx={{
              mb: 2,
              p: 2,
              cursor: 'pointer',
              transition: 'all 0.2s',
              borderColor: selectedContactId === contact.id ? 'primary.main' : 'divider',
              backgroundColor: selectedContactId === contact.id ? 'action.selected' : 'background.paper',
              '&:hover': {
                borderColor: 'primary.main',
              }
            }}
            onClick={() => onContactChange(contact.id)}
          >
            <Box display="flex" gap={2} alignItems="flex-start">
              <FormControlLabel
                value={contact.id}
                control={<Radio />}
                label=""
                sx={{ mt: 0, mb: 0 }}
              />
              <Box flex={1}>
                <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
                  {contact.name}
                </Typography>
                {contact.title && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {contact.title}
                  </Typography>
                )}
                <Typography variant="body2" gutterBottom>
                  {contact.email}
                </Typography>
                {contact.phone_number && (
                  <Typography variant="body2" color="text.secondary">
                    {contact.phone_number}
                  </Typography>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenModal(contact);
                }}
              >
                <EditIcon />
              </IconButton>
            </Box>
          </Paper>
        ))}
      </RadioGroup>

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => handleOpenModal()}
        fullWidth
      >
        Add New Customer Contact
      </Button>

      {/* Add/Edit Contact Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingContact ? 'Edit Customer Contact' : 'Add New Customer Contact'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Phone Number"
              value={formData.phone_number}
              onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid() || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : (editingContact ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrustedContactSelector; 